// Story 11.2 — Layout dagre (TB) que cumple AC #5 literalmente. Dagre se
// importa estático: cuando `pnpm install` baja la dep, el layout es
// inmediato; sin la dep, el bundle falla — el usuario debe instalar deps.
//
// Resuelve P-360 + P-364 del review (AC #5 mandaba dagre@^0.8 con
// rankdir:'TB', nodesep:60, ranksep:80; el layout ad-hoc original drifteaba
// de la spec y dejaba `dagre` en package.json como dep muerta).
//
// Para los tests determinísticos (sin deps instaladas) seguimos exponiendo
// `simpleRankLayout` desde `layout.ts` y consumiendo en `layout.test.ts`.
// `layoutGraph` (export canónico) ahora invoca dagre — el componente
// `FlowDiagram.tsx` recibe el layout dagre en runtime.

import dagre from 'dagre';
import type { DiagramGraph } from './flow-to-graph';
import type { LayoutOptions, PositionedGraph, PositionedNode } from './layout';

const DEFAULTS = {
  nodeWidth: 180,
  nodeHeight: 60,
  nodesep: 60,
  ranksep: 80,
} as const;

/**
 * Layout vertical top-down con dagre.
 *
 * Comportamiento:
 *   - `rankdir: 'TB'` (top-bottom).
 *   - `align: 'DL'` cuando hay >50 nodos (mejor compactness — AC #5).
 *   - Dagre devuelve `x, y` del centro del nodo; Reactflow espera top-left
 *     (resta width/2, height/2 — Dev Notes line 188).
 *   - Si dagre tira (e.g. ciclo non-trivial), retorna grafo sin posiciones
 *     explícitas (caller cae al `simpleRankLayout` fallback).
 */
export function dagreLayout(graph: DiagramGraph, options: LayoutOptions = {}): PositionedGraph {
  const opts = { ...DEFAULTS, ...options };
  const g = new dagre.graphlib.Graph();
  g.setGraph({
    rankdir: 'TB',
    nodesep: opts.nodesep,
    ranksep: opts.ranksep,
    align: graph.nodes.length > 50 ? 'DL' : undefined,
  });
  g.setDefaultEdgeLabel(() => ({}));

  for (const n of graph.nodes) {
    g.setNode(n.id, { width: opts.nodeWidth, height: opts.nodeHeight });
  }
  for (const e of graph.edges) {
    g.setEdge(e.source, e.target);
  }

  dagre.layout(g);

  const positioned: PositionedNode[] = graph.nodes.map((n) => {
    const node = g.node(n.id);
    return {
      ...n,
      width: opts.nodeWidth,
      height: opts.nodeHeight,
      position: {
        // dagre centra el nodo; reactflow espera top-left.
        x: (node?.x ?? 0) - opts.nodeWidth / 2,
        y: (node?.y ?? 0) - opts.nodeHeight / 2,
      },
    };
  });
  return { nodes: positioned, edges: graph.edges };
}
